import {
  careInsuranceLevelOptions,
  careInsuranceStatusOptions,
  disabilityMedicalCertOptions,
  intractableDiseaseCapOptions,
  medicalInsuranceCopayOptions,
  medicalInsuranceIncomeClassOptions,
  medicalInsuranceTypeOptions,
  pendingDoneStatusOptions,
  pharmacyStatusOptions,
} from '../../constants/patient';
import { isMedicalDevicesEmpty, type MedicalDevices } from '../../constants/patientMedicalDevices';
import type { OtherCareServices, Patient } from '../../constants/patient';

const mapOpts = (opts: { label: string; value: string }[], v: string | null | undefined) =>
  opts.find((o) => o.value === v)?.label ?? v ?? '—';

export function formatMedicalDevices(d: MedicalDevices): string {
  if (isMedicalDevicesEmpty(d)) return 'なし';
  const lines: string[] = [];
  if (d.oxygen?.length || d.oxygen_note?.trim()) {
    const parts = [d.oxygen?.length ? d.oxygen.join(', ') : null, d.oxygen_note?.trim() || null].filter(
      Boolean,
    );
    lines.push(`酸素: ${parts.join(' / ')}`);
  }
  if (d.insulin) lines.push(`インスリン: ${d.insulin}`);
  if (d.blood_glucose_per_day) lines.push(`血糖測定: ${d.blood_glucose_per_day}`);
  if (d.infusion?.length) lines.push(`点滴: ${d.infusion.join(', ')}`);
  if (d.orthopedic_injection) lines.push(`整形外科注射: ${d.orthopedic_injection}`);
  if (d.gastrostomy) lines.push(`胃瘻: ${d.gastrostomy}`);
  if (d.enterostomy) lines.push(`腸瘻: ${d.enterostomy}`);
  if (d.tracheostomy_tube) lines.push(`気切チューブ: ${d.tracheostomy_tube}`);
  if (d.stoma) lines.push(`ストマ: ${d.stoma}`);
  return lines.join('\n') || 'なし';
}

export function labelMedicalInsuranceType(v: Patient['medical_insurance_type']) {
  return mapOpts(medicalInsuranceTypeOptions, v ?? undefined);
}

export function labelMedicalInsuranceIncomeClass(v: Patient['medical_insurance_income_class']) {
  return mapOpts(medicalInsuranceIncomeClassOptions, v ?? undefined);
}

export function labelMedicalInsuranceCopay(v: Patient['medical_insurance_copay']) {
  return mapOpts(medicalInsuranceCopayOptions, v ?? undefined);
}

export function labelIntractableDiseaseCap(v: Patient['intractable_disease_cap']) {
  return mapOpts(intractableDiseaseCapOptions, v ?? undefined);
}

export function labelDisabilityMedicalCert(v: Patient['disability_medical_cert']) {
  return mapOpts(disabilityMedicalCertOptions, v ?? undefined);
}

export function labelCareInsurance(v: Patient['care_insurance_status']) {
  return mapOpts(careInsuranceStatusOptions, v ?? undefined);
}

export function labelCareLevel(v: Patient['care_insurance_level']) {
  return mapOpts(careInsuranceLevelOptions, v ?? undefined);
}

export function labelPharmacy(v: Patient['pharmacy_status']) {
  return mapOpts(pharmacyStatusOptions, v ?? undefined);
}

/** 患者情報画面: かかりつけ薬局（なし / 薬局名 / あり（未入力）） */
export function formatPharmacyDisplay(
  status: Patient['pharmacy_status'],
  name: Patient['pharmacy_name'],
): string | null {
  if (!status) return null;
  if (status === 'none') return 'なし';
  if (status === 'has') {
    const trimmed = name?.trim();
    return trimmed ? trimmed : 'あり（未入力）';
  }
  return null;
}

function labelYesNo(v: 'yes' | 'no' | undefined): string | null {
  if (v === 'yes') return 'あり';
  if (v === 'no') return 'なし';
  return null;
}

/** 有無＋備考（備考があるときは2行目） */
export function formatYesNoWithNote(
  value: 'yes' | 'no' | undefined,
  note: string | undefined,
): string | null {
  const status = labelYesNo(value);
  if (!status) return null;
  const trimmedNote = note?.trim();
  return trimmedNote ? `${status}\n${trimmedNote}` : status;
}

export function formatOtherCareServiceRow(
  services: OtherCareServices,
  key: 'visit_rehab' | 'day_service' | 'helper' | 'other',
  noteKey: 'visit_rehab_note' | 'day_service_note' | 'helper_note' | 'other_note',
): string | null {
  return formatYesNoWithNote(services[key], services[noteKey]);
}

export function labelRequest(v: Patient['face_sheet_request']) {
  return mapOpts(pendingDoneStatusOptions, v ?? undefined);
}
